import React, {Fragment} from 'react';

import {CallMade, Add} from '@material-ui/icons';
import {useHistory} from 'react-router-dom';
import {AppBar, Box, Divider, Grid} from '@material-ui/core';

import {EfeAccordion, EfeButtonPrimary, EfeButtonSecondary} from 'components';
import {
    EfeResponse,
    EfeViewsArray,
    LegalRepresentativesResponse,
    RealOwnerAdditionalInformation,
} from 'commons/models';
import {getRandomHex, nextStep, useViewSearch} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {AppBarStyles} from 'assets/tss/commons';
import {MainContent} from 'layouts/commons';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {RepresentativeInfo} from 'commons/types/Types';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {HistoryRoutes} from 'commons/constants';
import {ClientService} from 'services';
import {useSpinner} from 'hooks';

/* eslint-disable react/jsx-handler-names */
function LegalRepresentative() {
    const classes = AppBarStyles();
    const {menu, fatherKycId, setKycId} = useMenuContext();
    const history = useHistory();
    const {representatives, selectedRepresentative, setSelectedRepresentative, setRepresentatives} =
        useRelatedPeopleContext();
    const [representativesSize, setRepresentativesSize] = React.useState<number>(1);
    const [showViews] = React.useState<{value: boolean}>({value: false});
    const {flow, updateModuleStep} = useFlowContext();
    const views: Array<EfeViewsArray> = useViewSearch({...flow}, [...menu]);
    const [viewKey, setViewKey] = React.useState<string>(
        `View-${getRandomHex()}`
    );
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (
            representatives &&
            representatives.length === representativesSize &&
            selectedRepresentative < 0
        ) {
            showViews.value = false;
            setRepresentativesSize(representatives.length + 1);
        }
    }, [representatives]);

    async function handleChange(idkyc: number) {
        await setKycId(-1);
        await setSelectedRepresentative(-1);
        await setViewKey(`View-${getRandomHex()}`);
        showViews.value = false;
        await setKycId(idkyc);
        await setSelectedRepresentative(idkyc);
        showViews.value = true;
        history.push(HistoryRoutes.LEGAL_REPRESENTATIVE);
    }

    React.useEffect(() => {
        if (fatherKycId > 0 && selectedRepresentative < 0) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<LegalRepresentativesResponse> =
                        await ClientService.getLegalRepresentatives(fatherKycId);
                    if (response && response.data) {
                        showViews.value = false;
                        const tempRepresentatives: RepresentativeInfo[] = [];
                        for (const item of response.data.data) {
                            const response: EfeResponse<RealOwnerAdditionalInformation> =
                                await ClientService.getRealOwnerAdditionalInformation(item.kycId);
                            let traded = false;
                            if (response && response.data) {
                                traded = response.data.traded;
                            }
                            tempRepresentatives.push({
                                kycId: item.kycId,
                                traded: traded,
                                completeName: `${item.name} ${item.lastName} ${item.secondLastName}`,
                                foreignCitizen: item.foreignCitizen,
                            });
                        }
                        setRepresentatives(tempRepresentatives);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                }
            })();
        }
    }, []);

    function handleSubmit() {
        const path = nextStep(flow, menu, true);
        updateModuleStep(false);
        history.push(path);
        setSelectedRepresentative(-1);
        setKycId(-1);
    }

    async function handleNew() {
        await setKycId(-1);
        showViews.value = false;
        await setViewKey(`View-${getRandomHex()}`);
        await setSelectedRepresentative(-1);
        showViews.value = true;
        history.push(HistoryRoutes.LEGAL_REPRESENTATIVE);
    }

    return (
        <Fragment>
            {isLoading && useSpinner(isLoading)}
            <MainContent>
                <Grid container direction='row' justifyContent='space-between'>
                    {!isLoading &&
                        representatives &&
                        representatives.length > 0 &&
                        representatives.map((representative: RepresentativeInfo, index: number) => {
                            return (
                                <Grid item xs={12} key={`representatives-${index}`}>
                                    <EfeAccordion
                                        onChange={() => handleChange(representative.kycId)}
                                        values={[
                                            {
                                                icon: <ActionIcon />,
                                                title: representative.completeName,
                                                reactElement: <div></div>,
                                                expandIcon: false,
                                                disabled: false,
                                                expanded: false,
                                            },
                                        ]}
                                    />
                                </Grid>
                            );
                        })}
                </Grid>
            </MainContent>
            <Divider variant='middle' />
            <Grid container direction='row' justifyContent='space-between' key={viewKey}>
                {(!isLoading && (showViews.value || selectedRepresentative > 0)) &&
                    views &&
                    views.length > 0 &&
                    views.map((view: EfeViewsArray) => {
                        return (
                            <Grid item xs={12} key={view.index}>
                                {view.view}
                            </Grid>
                        );
                    })}
            </Grid>

            <AppBar position='fixed' color='primary' className={classes.appBar}>
                <MainContent>
                    <Grid container direction='row' justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Box display='flex' flexDirection='row' justifyContent='center' m={2}>
                                <Grid item xs={12}>
                                    <Box m={2}>
                                        <EfeButtonSecondary
                                            fullWidth
                                            variant='outlined'
                                            onClick={handleNew}
                                            endIcon={<Add />}>
                                            {'Agregar representante'}
                                        </EfeButtonSecondary>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box m={2}>
                                        <EfeButtonPrimary
                                            fullWidth
                                            disabled={!representatives || representatives.length === 0}
                                            onClick={handleSubmit}
                                            endIcon={<CallMade />}>
                                            {'Guardar y continuar'}
                                        </EfeButtonPrimary>
                                    </Box>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </MainContent>
            </AppBar>
        </Fragment>
    );
}

export default LegalRepresentative;
